import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HrefResolverService } from '@blk/cwp-ng-framework';
import { ApplicationPathConstants } from '@ddt-web/config';
import { ApplicationNames } from '@ddt-web/domain';
import * as castArray from 'lodash/castArray';
import * as compact from 'lodash/compact';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import * as isNil from 'lodash/isNil';
import * as omitBy from 'lodash/omitBy';

import { UrlBuilder } from '../builder';
import { PartnerQueryParamKey, PeQueryParamKey, SharedQueryParamKey } from '../constants/query-param.constants';
import { PortfolioHoldingsResolutionPreviousState, PortfolioHoldingsResolutionStateData } from '../model';
import { PortfolioEditorRedirectParams } from '../model/portfolio-editor-redirect-params.interface';
import { UrlUtils } from '../utils/url.utils';
import { UrlQueryParamService } from './url-query-param.service';

@Injectable({
	providedIn: 'root'
})
export class UrlRedirectService {

	constructor(
		private router: Router,
		private urlQueryParamService: UrlQueryParamService,
		private hrefResolver: HrefResolverService
	) { }

	public redirectIfInvalid(): void {
		if (!this.urlQueryParamService.queryParams ||
			!(this.urlQueryParamService.queryParams.portfolios || this.urlQueryParamService.queryParams.engagement || this.urlQueryParamService.queryParams.externalPortfolios)) {
			alert('You must choose a portfolio before proceeding');
			this.redirectToNewAnalysisPage();
		}
	}

	public redirectToRoute(route: string): void {
		this.router.navigateByUrl(route);
	}

	public redirectToEditPage(id: string): void {
		const testPartnerFirm = get(this.urlQueryParamService.queryParams, PartnerQueryParamKey.TEST_PARTNER_FIRM);
		const fee = this.urlQueryParamService.queryParams?.fee;
		const excludeFee = this.urlQueryParamService.queryParams?.excludeFee;

		this.router.navigate([ApplicationPathConstants.EDIT_PAGE_PATH], {
			queryParams: this.urlQueryParamService.transformParamsToObject(Object.assign({}, { portfolios: id }, { testPartnerFirm, fee, excludeFee }))
		});
	}

	public redirectToRebalancePage(id: string): void {
		this.router.navigate([ApplicationPathConstants.REBALANCE_PAGE_PATH], {
			queryParams: this.urlQueryParamService.transformParamsToObject({ portfolios: id })
		});
	}

	public redirectToBpasEngagementPage(id: string): Promise<boolean> {
		return this.router.navigate(['/'], {
			queryParams: this.urlQueryParamService.transformParamsToObject({
				engagement: id
			})
		});
	}

	public redirectToBpasAnalysis(id: string): void {
		this.router.navigate([ApplicationPathConstants.BPAS_ANALYSIS_PATH], {
			queryParams: this.urlQueryParamService.transformObjectToParams({
				engagement: id
			})
		});
	}

	public redirectToPcAnalysis(ids: string[], currency?: string, benchmarkId?: string): void {
		this.router.navigate([ApplicationPathConstants.PC_ANALYSIS_PATH], {
			queryParams: this.urlQueryParamService.transformObjectToParams({
				portfolios: ids,
				currency: currency,
				benchmarkId: benchmarkId
			})
		});
	}

	public redirectToPcAnalysisUsingWindow(path: string, ids: string[], currency?: string, benchmarkId?: string): void {
		const analysisPageUrl =
			path +
			'/analysis/overview?portfolios=list,' + ids +
			'&currency=' + currency +
			'&benchmarkId=' + benchmarkId;

		window.location.assign(analysisPageUrl);
	}

	public redirectTo360AnalysisUsingWindow(path: string, ids: string[], openInNewWindow?: boolean): void {
		const analysisPageUrl = UrlBuilder.construct().setPath(path + '/' + ApplicationPathConstants.PA_PATHS.OVERVIEW)
			.addParam('portfolios', [ids])
			.build();

		if (openInNewWindow) {
			window.open(analysisPageUrl.toString());
		} else {
			window.location.assign(analysisPageUrl.toString());
		}
	}

	public redirectToOverviewTab(ids: string[], reload: boolean = false, shouldRetainOtherQueryParam: boolean = false, additionalParams: any = {}): void {
		ids = Array.from(new Set(ids));
		const testPartnerFirm = get(this.urlQueryParamService.queryParams, PartnerQueryParamKey.TEST_PARTNER_FIRM);
		const queryParams = shouldRetainOtherQueryParam ?
			this.urlQueryParamService.transformObjectToParams(
				Object.assign(
					{},
					this.urlQueryParamService.queryParams,
					{ portfolios: ids }
				))
			:
			this.urlQueryParamService.transformObjectToParams(Object.assign({}, { portfolios: ids }, testPartnerFirm ? { testPartnerFirm: testPartnerFirm } : {}));

		if (!isEmpty(additionalParams)) { Object.assign(queryParams, additionalParams); }
		this.router.navigate([ApplicationPathConstants.OVERVIEW_TAB_PATH], {
			queryParams: queryParams
		}).then(() => {
			if (reload) {
				window.location.reload();
			} else {
				UrlUtils.resetScroll();
			}
		});
	}

	public redirectToScenarioTesterAnalyse(ids: string[], assetClasses: string[], stressTests: string[]): void {
		const options = {
			portfolios: ids,
			assetClass: assetClasses,
			stressTest: stressTests
		};
		this.router.navigate([ApplicationPathConstants.SCENARIO_TESTER_ANALYSE], {
			queryParams: this.urlQueryParamService.transformObjectToParams(options)
		}).then(() => {
			UrlUtils.resetScroll();
		});
	}

	public redirectToHoldingsCharacteristicsTab(ids: string[]): void {
		ids = Array.from(new Set(ids));
		this.router.navigate([ApplicationPathConstants.HOLDINGS_CHARACTERISTICS_TAB_PATH], {
			queryParams: this.urlQueryParamService.transformObjectToParams({
				portfolios: ids
			})
		});
	}

	public redirectToCmaEstimatorAnalysis(portfolioId: string, goal?: string, target?: string): void {
		this.router.navigate([ApplicationPathConstants.CMA_ANALYSIS_PATH], {
			queryParams: this.urlQueryParamService.transformObjectToParams({
				portfolios: portfolioId,
				goal,
				target
			})
		});
	}

	public redirectToIqAnalysis(ids: string[]) {
		ids = Array.from(new Set(ids));
		this.router.navigate([ApplicationPathConstants.INVESTMENT_QUALITY_PATHS.ANALYSIS], {
			queryParams: this.urlQueryParamService.transformObjectToParams({
				portfolios: ids
			})
		}).then(() => {
			UrlUtils.resetScroll();
		});
	}

	public redirectToIqInvestmentDetails(ids: string[], statusOrAof: string) {
		ids = Array.from(new Set(ids));
		const url = `/${ApplicationPathConstants.INVESTMENT_QUALITY_PATHS.ANALYSIS}/${ApplicationPathConstants.INVESTMENT_QUALITY_PATHS.INVESTMENT_DETAILS}/${statusOrAof}`;
		this.router.navigate([url], {
			queryParams: this.urlQueryParamService.transformObjectToParams({
				portfolios: ids
			})
		}).then(() => {
			UrlUtils.resetScroll();
		});
	}

	public redirectToMainPage(application: string): Promise<boolean> {
		let redirectSlice = '';
		switch (application) {
			case ApplicationNames.SCENARIO_TESTER:
				redirectSlice = ApplicationPathConstants.SCENARIO_TESTER_ANALYSE;
				break;
			case ApplicationNames.PORTFOLIO_ANALYSER:
				redirectSlice = this.urlQueryParamService.queryParams ? ApplicationPathConstants.OVERVIEW_TAB_PATH : '/';
				break;
			case ApplicationNames.MY_SAVED_PORTFOLIOS:
			case ApplicationNames.TAX_ESTIMATOR:
			case ApplicationNames.PROFESSIONAL_INVESTORS:
			case ApplicationNames.AC_PORTFOLIO_LIBRARY:
			case ApplicationNames.PORTFOLIO_CENTRE:
				this.urlQueryParamService.queryParams = {};
				redirectSlice = '/';
				break;
			case ApplicationNames.PORTFOLIO_ANALYSIS_CONSTRUCTION:
				redirectSlice = this.urlQueryParamService.queryParams ? ApplicationPathConstants.BPAS_ANALYSIS_PATH : '/';
				break;
			case ApplicationNames.MODELS_BROWSE:
				redirectSlice = this.urlQueryParamService.queryParams ? ApplicationPathConstants.MODELS_BROWSE_PATHS.FAMILY : '/';
				break;
			case ApplicationNames.PORTFOLIO_CENTRE_360:
				redirectSlice = ApplicationPathConstants.PC_ANALYSIS_PATH;
				break;
			case ApplicationNames.CMA_ESTIMATOR:
				redirectSlice = this.urlQueryParamService.queryParams ? ApplicationPathConstants.CMA_ESTIMATOR_PATHS.ANALYSIS : '/';
				break;
			case ApplicationNames.INVESTMENT_QUALITY:
				redirectSlice = this.urlQueryParamService.queryParams ? ApplicationPathConstants.INVESTMENT_QUALITY_PATHS.ANALYSIS : '/';
				break;
			case ApplicationNames.IRETIRE_ACCUM:
			case ApplicationNames.IRETIRE_DECUM:
				const url = this.router.url;
				redirectSlice = `/${url.split('/')[1]}/${ApplicationPathConstants.IRETIRE_PATHS.RESULTS}`;
				break;
			case ApplicationNames.MODELS_EXPERIENCE:
				redirectSlice = this.urlQueryParamService.queryParams ? ApplicationPathConstants.MODELS_EXPERIENCE_PATHS.FAMILY : '/';
				break;
			default:
				throw new Error('Redirect to main page - application not recognized');
		}

		return this.router.navigate([redirectSlice], {
			queryParams: this.urlQueryParamService.transformObjectToParams(this.urlQueryParamService.queryParams)
		});
	}

	public redirectToPeerComparison(ids: string[]): void {
		ids = Array.from(new Set(ids));
		this.router.navigate([ApplicationPathConstants.PEER_COMPARISON_PATH], {
			queryParams: this.urlQueryParamService.transformObjectToParams({
				portfolios: ids
			})
		});
	}

	public redirectToNewAnalysisPage(): Promise<boolean> {
		const testPartnerFirm = get(this.urlQueryParamService.queryParams, PartnerQueryParamKey.TEST_PARTNER_FIRM);
		const queryParams = this.urlQueryParamService.transformObjectToParams(Object.assign({}, testPartnerFirm ? { testPartnerFirm: testPartnerFirm } : {}));

		return this.router.navigate([ApplicationPathConstants.NEW_ANALYSIS_PAGE_PATH], { queryParams });
	}

	public redirectToGenerateReportPage(): void {
		this.router.navigate([ApplicationPathConstants.GENERATE_REPORT], {
			queryParams: this.urlQueryParamService.transformObjectToParams(this.urlQueryParamService.queryParams)
		});
	}

	public redirectToPortfolioEditorView(params: PortfolioEditorRedirectParams): Promise<boolean> {
		const options = { queryParams: {} };
		const queryParams = {};
		const portfolios = compact(castArray(params?.portfolioIds));

		queryParams['portfolios'] = portfolios.length ? portfolios : null;
		queryParams['mode'] = params?.mode;
		queryParams['currency'] = params?.currency;
		queryParams['from'] = params?.origin;
		queryParams['tab'] = params?.tab;

		/*
		* This flag is to stop the portfolio saving from PE and pass the portfolio to the subject within the data-bridging.service.ts,
		* where the caller application would subscribe to the subject. This is only for saving a new portfolio, not editing an old portfolio.
		*/
		queryParams[SharedQueryParamKey.ENGAGEMENT] = params?.[SharedQueryParamKey.ENGAGEMENT];

		// holdings from URL
		queryParams[PeQueryParamKey.HOLDINGS] = this.urlQueryParamService?.queryParams?.[PeQueryParamKey.HOLDINGS];

		// make testPartnerFirm param persist when navigating to portfolio editor
		queryParams[PartnerQueryParamKey.TEST_PARTNER_FIRM] = this.urlQueryParamService?.queryParams?.[PartnerQueryParamKey.TEST_PARTNER_FIRM];

		const validQueryParams = omitBy(queryParams, value => isNil(value) || value === '');
		options.queryParams = this.urlQueryParamService.transformObjectToParams(validQueryParams);

		if (params?.standaloneApp) {
			const isBeta: boolean = window.location.href.includes('-beta');
			const peUrl = isBeta ? `standalonePortfolioEditorPageBeta` : 'standalonePortfolioEditorPage';
			let pePage = this.hrefResolver.getResolvedUrl(peUrl);

			if (!isEmpty(options?.queryParams)) {
				pePage += '?' + new URLSearchParams(options.queryParams).toString();
			}

			window.location.assign(pePage);
		} else {
			return this.router.navigate([ApplicationPathConstants.PORTFOLIO_EDITOR_PATH], options);
		}
	}

	public redirectToHoldingsResolutionPage(): Promise<boolean> {
		return this.router.navigate([ApplicationPathConstants.PORTFOLIO_EDITOR_PATH, ApplicationPathConstants.PORTFOLIO_EDITOR_PATHS.HOLDINGS_RESOLUTION]);
	}

	public redirectToExternalPortfolioHoldingsResolutionPage(
		holdingsResolutionState: PortfolioHoldingsResolutionStateData
	): Promise<boolean> {
		const qps = this.urlQueryParamService.queryParams;
		const previousState: PortfolioHoldingsResolutionPreviousState = {
			...holdingsResolutionState.previousState,
			queryParams: qps
		};
		holdingsResolutionState.previousState = previousState;
		return this.router.navigate(
			[ApplicationPathConstants.PARTNER_FLOW_PATHS.HOLDINGS_RESOLUTIONS],
			{ state: holdingsResolutionState }
		);
	}

	public redirectToTabByQueryParam(ids: string[], availablePathName: { [pathKey: string]: string }, shouldRetainOtherQueryParam: boolean = false, extraQueryParams: { [id: string]: any } = {}): void {
		ids = Array.from(new Set(ids));
		const tabName = get(this.urlQueryParamService.queryParams, 'tab');
		const tabNameExisted = tabName && Object.values(availablePathName).find(path => path === tabName);
		const queryParams = shouldRetainOtherQueryParam ?
			this.urlQueryParamService.transformObjectToParams(
				Object.assign(
					{},
					this.urlQueryParamService.queryParams,
					{ portfolios: ids },
					extraQueryParams
				))
			:
			this.urlQueryParamService.transformObjectToParams({ portfolios: ids });
		if (tabNameExisted) {
			this.router.navigate([tabName], {
				queryParams: queryParams,
				fragment: this.urlQueryParamService.queryParams?.['section']
			});
		}
		else {
			this.redirectToOverviewTab(ids, false, shouldRetainOtherQueryParam, extraQueryParams);
		}
	}

	public redirectWithQueryParam(url: string, ids: string[]): void {
		ids = Array.from(new Set(ids));
		this.router.navigate([url], {
			queryParams: this.urlQueryParamService.transformObjectToParams({
				portfolios: ids
			})
		}).then(() => {
			UrlUtils.resetScroll();
		});
	}

	public redirectToUrlWithQueryParam(url: string, ids: string[], shouldReload = false, shouldRetainOtherQueryParam = true): void {
		const queryParams = this.urlQueryParamService.transformObjectToParams({ portfolios: ids });
		this.router.navigate([url], { queryParams, queryParamsHandling: shouldRetainOtherQueryParam ? 'merge' : '' })
			.then(() => {
				if (shouldReload) {
					window.location.reload();
				} else {
					UrlUtils.resetScroll();
				}
			});
	}

	public redirectWithAllQueryParam(url: string): void {
		this.router.navigate([url], {
			queryParams: this.urlQueryParamService.queryParams
		});
	}

	public redirectToPIWithState(action?: string): void {
		const url = '/';
		this.router.navigate([url], {
			state: { action }
		});
	}

	public redirectToMonitoring(): void {
		const url = '/' + ApplicationPathConstants.AC_PORTFOLIO_LIBRARY_PATHS.MONITORING;
		this.router.navigate([url]);
	}

	public redirectToImportPartnerPortfoliosWithState(portfolioHoldingsResolutionPreviousState: PortfolioHoldingsResolutionPreviousState, shouldRetainQueryParams: boolean = true) {
		let queryParams = isEmpty(portfolioHoldingsResolutionPreviousState.queryParams)
			? this.urlQueryParamService.queryParams : portfolioHoldingsResolutionPreviousState.queryParams;

		queryParams = this.urlQueryParamService.transformObjectToParams(queryParams);

		this.router.navigate(
			[portfolioHoldingsResolutionPreviousState.routeOrigin],
			{
				state: { previousState: portfolioHoldingsResolutionPreviousState },
				queryParams: shouldRetainQueryParams ? queryParams : null
			}
		);
	}
}
